/**
 * Smart Recommendations Service
 * 
 * Provides intelligent recommendations based on:
 * - User's past trips and preferences
 * - Similar trips from other users (collaborative filtering)
 * - Trending places in destination
 * - Contextual factors (budget, travel style, proximity)
 */

import { collection, query, where, getDocs, orderBy, limit, Timestamp, doc, setDoc, deleteDoc, onSnapshot, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Trip } from './trips';
import type { ItineraryItem } from './itinerary';
import { fetchTripItineraryItems } from './itinerary';
import { getTrendingPlaces, type PlaceRatingAggregate } from './placeRatings';
import type { LocationCoordinates } from './maps';
import { calculateDistance } from './analytics';
import type { TripRecommendation, CostBreakdown, PerTravelerCosts } from './aiPlanner';
import type { TripTemplate } from '@/types/tripTemplates';
import { logger } from './logger';
import { performanceMonitor } from '@/utils/performance';

function asBudgetRange(b: unknown): { min: number; max: number } | null {
  if (typeof b === 'number' && Number.isFinite(b) && b > 0) {
    const pad = Math.max(b * 0.2, 1);
    return { min: Math.max(0, b - pad), max: b + pad };
  }
  if (typeof b === 'object' && b !== null && 'min' in b && 'max' in b) {
    const min = Number((b as { min: unknown }).min);
    const max = Number((b as { max: unknown }).max);
    if (Number.isFinite(min) && Number.isFinite(max)) return { min, max };
  }
  return null;
}

function firestoreTimestampToDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value && typeof value === 'object' && typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  return new Date(value as string | number);
}

export type RecommendationContext = {
  tripId: string;
  userId: string;
  trip: Trip;
  currentLocation?: LocationCoordinates;
  selectedDay?: number;
  budget?: { min?: number; max?: number; currency?: string };
  travelStyles?: string[];
};

export type PlaceRecommendation = {
  place: PlaceRatingAggregate | {
    name: string;
    address?: string;
    coordinates?: LocationCoordinates;
    category?: string;
  };
  reason: string;
  relevanceScore: number;
  source: 'similar_trips' | 'trending' | 'user_history' | 'proximity' | 'ai';
};

export type SimilarTrip = {
  trip: Trip;
  similarityScore: number;
  matchingFactors: string[];
};

/**
 * Find trips similar to the current trip
 */
export const getSimilarTrips = async (
  currentTrip: Trip,
  limitCount: number = 10
): Promise<SimilarTrip[]> => {
  return performanceMonitor.measure('getSimilarTrips', async () => {
    if (!currentTrip?.owner || !currentTrip?.destination) {
      logger.warn('[Recommendations] Skipping similar trips due to missing owner or destination', {
        tripId: currentTrip?.id,
        hasOwner: Boolean(currentTrip?.owner),
        hasDestination: Boolean(currentTrip?.destination),
      });
      return [];
    }
    try {
      const similarTrips: SimilarTrip[] = [];
      const matchingFactors: Map<string, string[]> = new Map();

      // Extract trip characteristics
      const currentDestination = currentTrip.destination || currentTrip.destinations?.[0]?.city || '';
      const currentDuration = Math.ceil(
        (currentTrip.endDate.getTime() - currentTrip.startDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      const currentTravelStyles = currentTrip.travelStyles || [];

      // Query public trip summaries with similar destination.
      // Full trip docs are intentionally member-restricted in Firestore rules.
      const tripsRef = collection(db, 'publicTripSummaries');
      const destinationQuery = query(
        tripsRef,
        where('destination', '==', currentDestination),
        orderBy('startDate', 'desc'),
        limit(limitCount * 3) // Get more to filter further
      );

      let snapshot;
      try {
        snapshot = await getDocs(destinationQuery);
      } catch (err) {
        logger.error('[Recommendations] Firestore query failed for similar trips', err instanceof Error ? err : new Error(String(err)));
        return [];
      }

      snapshot.forEach((doc) => {
        const tripData = doc.data() as any;
        const tripId = doc.id;

        // Skip current trip
        if (tripId === currentTrip.id) return;

        // Skip if trip is archived or private (unless we have permission)
        if (tripData.isArchived || (tripData.isPublic === false && tripData.owner !== currentTrip.owner)) {
          return;
        }

        // Calculate similarity score
        let similarityScore = 0;
        const factors: string[] = [];

        // Destination match (highest weight)
        if (tripData.destination === currentDestination) {
          similarityScore += 30;
          factors.push('destination');
        }

        // Duration similarity (within ±3 days)
        const tripDuration = tripData.startDate && tripData.endDate
          ? Math.ceil(
              (tripData.endDate.toDate?.().getTime() || new Date(tripData.endDate).getTime()) -
              (tripData.startDate.toDate?.().getTime() || new Date(tripData.startDate).getTime())
            ) / (1000 * 60 * 60 * 24)
          : 0;
        
        const durationDiff = Math.abs(tripDuration - currentDuration);
        if (durationDiff <= 3) {
          similarityScore += 20 - (durationDiff * 2);
          factors.push('duration');
        }

        // Budget similarity (within same range)
        const currentRange = asBudgetRange(currentTrip.budget);
        const otherRange = asBudgetRange(tripData.budget);
        if (currentRange && otherRange) {
          const budgetOverlap =
            Math.min(currentRange.max, otherRange.max) - Math.max(currentRange.min, otherRange.min);
          if (budgetOverlap > 0) {
            similarityScore += 15;
            factors.push('budget');
          }
        }

        // Travel style match
        const otherStyles = tripData.travelStyles || [];
        const styleMatches = currentTravelStyles.filter((style: string) =>
          otherStyles.includes(style)
        );
        if (styleMatches.length > 0) {
          similarityScore += styleMatches.length * 5;
          factors.push('travel_style');
        }

        // Season similarity (same month range)
        const currentMonth = currentTrip.startDate.getMonth();
        const otherMonth = tripData.startDate?.toDate?.()?.getMonth() || 
                          new Date(tripData.startDate).getMonth();
        if (Math.abs(currentMonth - otherMonth) <= 1) {
          similarityScore += 10;
          factors.push('season');
        }

        // Only include trips with meaningful similarity
        if (similarityScore >= 20) {
          const trip: Trip = {
            id: tripId,
            name: tripData.name || 'Untitled Trip',
            destination: tripData.destination,
            destinations: tripData.destinations,
            startDate: tripData.startDate?.toDate?.() || new Date(tripData.startDate),
            endDate: tripData.endDate?.toDate?.() || new Date(tripData.endDate),
            owner: tripData.owner,
            members: tripData.members || [],
            budget: tripData.budget,
            travelStyles: tripData.travelStyles || [],
            isPublic: tripData.isPublic ?? false,
            isArchived: tripData.isArchived ?? false,
            createdAt: tripData.createdAt?.toDate?.() || new Date(tripData.createdAt),
            baseCurrency: tripData.baseCurrency,
          };

          similarTrips.push({
            trip,
            similarityScore,
            matchingFactors: factors,
          });
        }
      });

      // Sort by similarity score and return top results
      return similarTrips
        .sort((a, b) => b.similarityScore - a.similarityScore)
        .slice(0, limitCount);
    } catch (error) {
      logger.error('[Recommendations] Error finding similar trips', error instanceof Error ? error : new Error(String(error)));
      return [];
    }
  }, { tripId: currentTrip.id, limitCount });
};

/**
 * Get recommendations from similar trips
 */
export const getRecommendationsFromSimilarTrips = async (
  context: RecommendationContext
): Promise<PlaceRecommendation[]> => {
  try {
    const similarTrips = await getSimilarTrips(context.trip, 5);
    const recommendations: PlaceRecommendation[] = [];
    const placeFrequency = new Map<string, {
      count: number;
      totalScore: number;
      tripIds: string[];
      firstSeen: string;
    }>();

    // Collect places from similar trips
    for (const similarTrip of similarTrips) {
      try {
        const items = await fetchTripItineraryItems(similarTrip.trip.id);
        
        items.forEach(item => {
          if (item.location || item.address) {
            const placeKey = (item.location || item.address || '').toLowerCase();
            const existing = placeFrequency.get(placeKey);
            
            if (existing) {
              existing.count += 1;
              existing.totalScore += similarTrip.similarityScore;
              existing.tripIds.push(similarTrip.trip.id);
            } else {
              placeFrequency.set(placeKey, {
                count: 1,
                totalScore: similarTrip.similarityScore,
                tripIds: [similarTrip.trip.id],
                firstSeen: item.location || item.address || '',
              });
            }
          }
        });
      } catch (error) {
        logger.warn(`[Recommendations] Error fetching items for trip ${similarTrip.trip.id}`, {
          tripId: similarTrip.trip.id,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    // Convert to recommendations
    placeFrequency.forEach((data, placeKey) => {
      const avgScore = data.totalScore / data.count;
      const relevanceScore = Math.min(100, (data.count * 10) + (avgScore * 0.5));

      recommendations.push({
        place: {
          name: data.firstSeen,
          address: data.firstSeen,
          category: 'place',
        },
        reason: `${data.count} similar trip${data.count > 1 ? 's' : ''} visited this place`,
        relevanceScore,
        source: 'similar_trips',
      });
    });

    return recommendations.sort((a, b) => b.relevanceScore - a.relevanceScore);
  } catch (error) {
    logger.error('[Recommendations] Error getting recommendations from similar trips', error instanceof Error ? error : new Error(String(error)));
    return [];
  }
};

/**
 * Get contextual recommendations based on current trip state
 */
export const getContextualRecommendations = async (
  context: RecommendationContext,
  query?: string
): Promise<PlaceRecommendation[]> => {
  const recommendations: PlaceRecommendation[] = [];

  try {
    // 1. Get trending places in destination
    const destination = context.trip.destination || 
                       context.trip.destinations?.[0]?.city || '';
    
    if (destination) {
      const trendingPlaces = await getTrendingPlaces(destination, 10, {
        proximity: context.currentLocation,
        maxDistanceKm: 50,
      });

      trendingPlaces.forEach((place, index) => {
        recommendations.push({
          place,
          reason: `Popular in ${destination}${place.averageRating >= 4.5 ? ' (highly rated)' : ''}`,
          relevanceScore: 80 - (index * 2), // Higher score for top results
          source: 'trending',
        });
      });
    }

    // 2. Get recommendations from similar trips
    const similarTripRecommendations = await getRecommendationsFromSimilarTrips(context);
    recommendations.push(...similarTripRecommendations);

    // 3. Filter by budget if specified
    if (context.budget) {
      recommendations.forEach(rec => {
        // Adjust score based on budget compatibility
        // (This would need place cost data - simplified for now)
        if (rec.place && 'averageRating' in rec.place) {
          // Higher rated places might be more expensive, adjust accordingly
          // This is a simplified heuristic
        }
      });
    }

    // 4. Filter by proximity if current location provided
    if (context.currentLocation && recommendations.length > 0) {
      recommendations.forEach(rec => {
        if (rec.place.coordinates) {
          const loc = context.currentLocation!;
          const coords = rec.place.coordinates;
          const distance = calculateDistance(loc.latitude, loc.longitude, coords.latitude, coords.longitude);
          
          // Boost score for nearby places
          if (distance < 5) {
            rec.relevanceScore += 10;
            rec.reason += ' (nearby)';
          } else if (distance < 20) {
            rec.relevanceScore += 5;
          }
        }
      });
    }

    // 5. Filter by query if provided
    if (query) {
      const queryLower = query.toLowerCase();
      recommendations.forEach(rec => {
        const placeLabel =
          'placeName' in rec.place ? rec.place.placeName : (rec.place as { name?: string }).name;
        const placeName = placeLabel?.toLowerCase() || '';
        if (placeName.includes(queryLower)) {
          rec.relevanceScore += 20;
        }
      });
    }

    // Sort by relevance and return top results
    const sorted = recommendations
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, 15);
    
    // If no recommendations found, return empty array instead of throwing
    // This allows the UI to show a helpful empty state
    return sorted;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    logger.error('[Recommendations] Error getting contextual recommendations', err);
    const __agentPayloadRecError = {
      sessionId:'debug-session',
      runId:'run1',
      hypothesisId:'H4',
      location:'recommendations.ts:getContextualRecommendations',
      message:'Contextual recommendations failed',
      data:{
        errorMessage: err.message,
        stack: err.stack,
        hasTripOwner: Boolean(context?.trip?.owner),
        tripId: (context as any)?.trip?.id,
        userId: context?.userId,
      },
      timestamp:Date.now()
    };
    return [];
  }
};

/**
 * Get user's past trip preferences
 */
export const getUserTripPreferences = async (
  userId: string
): Promise<{
  preferredDestinations: string[];
  averageBudget: { min: number; max: number };
  commonTravelStyles: string[];
  averageDuration: number;
}> => {
  try {
    const tripsRef = collection(db, 'trips');
    const userTripsQuery = query(
      tripsRef,
      where('owner', '==', userId),
      orderBy('startDate', 'desc'),
      limit(20) // Analyze last 20 trips
    );

    const snapshot = await getDocs(userTripsQuery);
    const destinations = new Set<string>();
    const budgets: Array<{ min: number; max: number }> = [];
    const travelStyles = new Map<string, number>();
    const durations: number[] = [];

    snapshot.forEach((doc) => {
      const tripData = doc.data() as any;

      // Collect destinations
      if (tripData.destination) {
        destinations.add(tripData.destination);
      }
      if (tripData.destinations) {
        tripData.destinations.forEach((dest: any) => {
          if (dest.city) destinations.add(dest.city);
        });
      }

      // Collect budgets
      if (tripData.budget) {
        budgets.push({
          min: tripData.budget.min || 0,
          max: tripData.budget.max || 0,
        });
      }

      // Collect travel styles
      if (tripData.travelStyles) {
        tripData.travelStyles.forEach((style: string) => {
          travelStyles.set(style, (travelStyles.get(style) || 0) + 1);
        });
      }

      // Collect durations
      if (tripData.startDate && tripData.endDate) {
        const start = tripData.startDate.toDate?.() || new Date(tripData.startDate);
        const end = tripData.endDate.toDate?.() || new Date(tripData.endDate);
        const duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        durations.push(duration);
      }
    });

    // Calculate averages
    const avgBudget = budgets.length > 0
      ? {
          min: budgets.reduce((sum, b) => sum + b.min, 0) / budgets.length,
          max: budgets.reduce((sum, b) => sum + b.max, 0) / budgets.length,
        }
      : { min: 0, max: 0 };

    const avgDuration = durations.length > 0
      ? durations.reduce((sum, d) => sum + d, 0) / durations.length
      : 7; // Default to 7 days

    const commonStyles = Array.from(travelStyles.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([style]) => style);

    return {
      preferredDestinations: Array.from(destinations),
      averageBudget: avgBudget,
      commonTravelStyles: commonStyles,
      averageDuration: avgDuration,
    };
  } catch (error) {
    logger.error('[Recommendations] Error getting user preferences', error instanceof Error ? error : new Error(String(error)));
    return {
      preferredDestinations: [],
      averageBudget: { min: 0, max: 0 },
      commonTravelStyles: [],
      averageDuration: 7,
    };
  }
};

/**
 * Saved Recommendations Functions
 */

export type SavedRecommendation = TripRecommendation & {
  id: string;
  savedAt: Date;
  sharedWith?: string[];
  /**
   * Optional source metadata so we can also store "saved templates" alongside AI recommendations.
   * Backwards compatible: older docs won't have these fields.
   */
  source?: 'ai' | 'template';
  templateId?: string;
  title?: string;
  coverImage?: string | null;
  budgetMin?: number;
  budgetMax?: number;
  budgetCurrency?: string;
};

type FirestoreSavedRecommendation = Omit<SavedRecommendation, 'savedAt'> & {
  savedAt: Timestamp;
};

/**
 * Subscribe to saved recommendations for a user
 */
export const subscribeToSavedRecommendations = (
  userId: string,
  callback: (recommendations: SavedRecommendation[]) => void
): (() => void) => {
  if (!userId) {
    logger.warn('[Recommendations] subscribeToSavedRecommendations called without userId');
    return () => {};
  }

  const savedRef = collection(db, 'users', userId, 'savedRecommendations');
  const savedQuery = query(savedRef, orderBy('savedAt', 'desc'));

  const unsubscribe = onSnapshot(
    savedQuery,
    (snapshot) => {
      const recommendations: SavedRecommendation[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data() as FirestoreSavedRecommendation;
        recommendations.push({
          ...data,
          id: doc.id,
          savedAt: firestoreTimestampToDate(data.savedAt),
        });
      });

      callback(recommendations);
    },
    (error) => {
      logger.error('[Recommendations] Error subscribing to saved recommendations', error instanceof Error ? error : new Error(String(error)));
      callback([]);
    }
  );

  return unsubscribe;
};

/**
 * Save a recommendation for a user
 */
export const saveRecommendation = async (
  userId: string,
  recommendation: TripRecommendation
): Promise<string> => {
  if (!userId) {
    throw new Error('User ID is required to save a recommendation');
  }

  try {
    const savedRef = collection(db, 'users', userId, 'savedRecommendations');
    const newDocRef = doc(savedRef);

    const savedData: Omit<FirestoreSavedRecommendation, 'id'> = {
      ...recommendation,
      source: 'ai',
      savedAt: serverTimestamp() as Timestamp,
    };

    await setDoc(newDocRef, savedData);
    
    logger.debug('[Recommendations] Recommendation saved', { recommendationId: newDocRef.id, userId });
    return newDocRef.id;
  } catch (error) {
    logger.error('[Recommendations] Error saving recommendation', error instanceof Error ? error : new Error(String(error)), { userId });
    throw error;
  }
};

/**
 * Save a template as a "saved recommendation" for a user (so templates appear in Saved Recommendations)
 */
export const saveTemplateRecommendation = async (
  userId: string,
  template: TripTemplate
): Promise<string> => {
  if (!userId) {
    throw new Error('User ID is required to save a template recommendation');
  }
  if (!template?.id) {
    throw new Error('Template ID is required to save a template recommendation');
  }

  try {
    const savedRef = collection(db, 'users', userId, 'savedRecommendations');
    const newDocRef = doc(savedRef);

    const budgetMin = template.estimatedBudget?.min ?? undefined;
    const budgetMax = template.estimatedBudget?.max ?? undefined;
    const budgetCurrency = template.estimatedBudget?.currency ?? undefined;
    const estimatedBudget =
      typeof budgetMin === 'number' && typeof budgetMax === 'number'
        ? Math.round((budgetMin + budgetMax) / 2)
        : undefined;

    const savedData: Omit<FirestoreSavedRecommendation, 'id'> = {
      source: 'template',
      templateId: template.id,
      title: template.title,
      destination: template.destination,
      description: template.shortDescription || `Template: ${template.title}`,
      duration: template.duration,
      estimatedBudget,
      coverImage: template.coverImage ?? null,
      budgetMin,
      budgetMax,
      budgetCurrency,
      savedAt: serverTimestamp() as Timestamp,
    };

    await setDoc(newDocRef, savedData);
    logger.debug('[Recommendations] Template recommendation saved', { recommendationId: newDocRef.id, userId, templateId: template.id });
    return newDocRef.id;
  } catch (error) {
    logger.error('[Recommendations] Error saving template recommendation', error instanceof Error ? error : new Error(String(error)), { userId, templateId: template.id });
    throw error;
  }
};

/**
 * Unsave (remove) a recommendation
 */
export const unsaveRecommendation = async (
  userId: string,
  recommendationId: string
): Promise<void> => {
  if (!userId || !recommendationId) {
    throw new Error('User ID and recommendation ID are required');
  }

  try {
    const savedRef = doc(db, 'users', userId, 'savedRecommendations', recommendationId);
    await deleteDoc(savedRef);
    
    logger.debug('[Recommendations] Recommendation unsaved', { recommendationId, userId });
  } catch (error) {
    logger.error('[Recommendations] Error unsaving recommendation', error instanceof Error ? error : new Error(String(error)), { recommendationId, userId });
    throw error;
  }
};

/**
 * Check if a recommendation is saved
 */
export const isRecommendationSaved = async (
  userId: string,
  destinationOrRecommendation: string | TripRecommendation
): Promise<{ isSaved: boolean; savedRecommendationId?: string }> => {
  const destination =
    typeof destinationOrRecommendation === 'string'
      ? destinationOrRecommendation
      : destinationOrRecommendation?.destination;
  if (!userId || !destination) {
    return { isSaved: false };
  }

  try {
    const savedRef = collection(db, 'users', userId, 'savedRecommendations');
    const savedQuery = query(savedRef, where('destination', '==', destination), limit(1));
    
    const snapshot = await getDocs(savedQuery);
    
    if (snapshot.empty) {
      return { isSaved: false };
    }

    return {
      isSaved: true,
      savedRecommendationId: snapshot.docs[0].id,
    };
  } catch (error) {
    logger.error('[Recommendations] Error checking if recommendation is saved', error instanceof Error ? error : new Error(String(error)), { userId, destination });
    return { isSaved: false };
  }
};

/**
 * Check if a template is already saved as a recommendation
 */
export const isTemplateRecommendationSaved = async (
  userId: string,
  templateId: string
): Promise<{ isSaved: boolean; savedRecommendationId?: string }> => {
  if (!userId || !templateId) {
    return { isSaved: false };
  }

  try {
    const savedRef = collection(db, 'users', userId, 'savedRecommendations');
    const savedQuery = query(savedRef, where('templateId', '==', templateId), limit(1));
    const snapshot = await getDocs(savedQuery);
    if (snapshot.empty) {
      return { isSaved: false };
    }
    return { isSaved: true, savedRecommendationId: snapshot.docs[0].id };
  } catch (error) {
    logger.error(
      '[Recommendations] Error checking if template recommendation is saved',
      error instanceof Error ? error : new Error(String(error)),
      { userId, templateId }
    );
    return { isSaved: false };
  }
};

/**
 * Update `sharedWith` on a saved recommendation (best-effort; rules must allow user writes).
 */
export const shareRecommendation = async (
  userId: string,
  recommendationId: string,
  companionIds: string[],
): Promise<void> => {
  if (!userId || !recommendationId) {
    throw new Error('User ID and recommendation ID are required');
  }
  const savedRef = doc(db, 'users', userId, 'savedRecommendations', recommendationId);
  await setDoc(savedRef, { sharedWith: companionIds }, { merge: true });
};

export const unshareRecommendation = async (userId: string, recommendationId: string): Promise<void> => {
  if (!userId || !recommendationId) {
    throw new Error('User ID and recommendation ID are required');
  }
  const savedRef = doc(db, 'users', userId, 'savedRecommendations', recommendationId);
  await setDoc(savedRef, { sharedWith: [] }, { merge: true });
};

/** Placeholder until trip-scoped companion discovery is implemented. */
export const getTripCompanions = async (_userId: string): Promise<string[]> => {
  void _userId;
  return [];
};
